import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom'; // ✅ CAMBIO: useParams en lugar de useSearchParams
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  LinearProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  VpnKey as VpnKeyIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { passwordResetService } from '../../services/passwordResetService';
import { BRAND_GRADIENT } from '../../constants/branding';
import EapLogo from '../../components/brand/EapLogo';

interface ValidationState {
  isValid: boolean;
  loading: boolean;
  error: string | null;
  userEmail: string | null;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>(); // ✅ CAMBIO: Usar useParams

  const [validation, setValidation] = useState<ValidationState>({
    isValid: false,
    loading: true,
    error: null,
    userEmail: null
  });

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordFieldFocused, setPasswordFieldFocused] = useState(false);

  // Validar token al cargar la página
  useEffect(() => {
    if (!token) {
      setValidation({
        isValid: false,
        loading: false,
        error: 'Token de restablecimiento no encontrado',
        userEmail: null
      });
      return;
    }

    validateToken();
  }, [token]);

  // Validar token con el backend
  const validateToken = async () => {
    try {
      const data = await passwordResetService.validateResetToken(token!);

      if (data.success) {
        setValidation({
          isValid: true,
          loading: false,
          error: null,
          userEmail: data.email || null
        });
      } else {
        setValidation({
          isValid: false,
          loading: false,
          error: data.message || 'Token inválido o expirado',
          userEmail: null
        });
      }
    } catch (error: any) {
      setValidation({
        isValid: false,
        loading: false,
        error: error.message || 'Error de conexión',
        userEmail: null
      });
    }
  };

  // Validar fortaleza de contraseña
  const validatePassword = (password: string): string[] => {
    const issues: string[] = [];
    
    if (password.length < 6) {
      issues.push('Mínimo 6 caracteres');
    }
    if (!/[A-Z]/.test(password)) {
      issues.push('Al menos una mayúscula');
    }
    if (!/[a-z]/.test(password)) {
      issues.push('Al menos una minúscula');
    }
    if (!/[0-9]/.test(password)) {
      issues.push('Al menos un número');
    }
    
    return issues;
  };

  // Verificar cada requisito individualmente
  const getPasswordRequirements = (password: string) => {
    return [
      {
        text: 'Mínimo 6 caracteres',
        met: password.length >= 6
      },
      {
        text: 'Al menos una letra mayúscula (A-Z)',
        met: /[A-Z]/.test(password)
      },
      {
        text: 'Al menos una letra minúscula (a-z)',
        met: /[a-z]/.test(password)
      },
      {
        text: 'Al menos un número (0-9)',
        met: /[0-9]/.test(password)
      }
    ];
  };

  // Calcular fortaleza de contraseña
  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    const issues = validatePassword(password);
    const strength = Math.max(0, 4 - issues.length);
    
    const strengthMap = {
      0: { label: 'Muy débil', color: 'error.main' },
      1: { label: 'Débil', color: 'warning.main' },
      2: { label: 'Regular', color: 'info.main' },
      3: { label: 'Buena', color: 'success.light' },
      4: { label: 'Excelente', color: 'success.main' }
    };
    
    return {
      strength: (strength / 4) * 100,
      ...strengthMap[strength as keyof typeof strengthMap]
    };
  };

  // Validar formulario
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else {
      const passwordRequirements = getPasswordRequirements(formData.newPassword);
      const unmetRequirements = passwordRequirements.filter(req => !req.met);
      
      if (unmetRequirements.length > 0) {
        newErrors.newPassword = `La contraseña no cumple todos los requisitos`;
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'La confirmación es requerida';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setAlert(null);

    try {
      const result = await passwordResetService.resetPassword(
        token!,
        formData.newPassword,
        formData.confirmPassword
      );

      if (result.ok && result.data.success) {
        setSuccess(true);
        setAlert({
          type: 'success',
          message: result.data.message
        });
        
        // ✅ MEJORADO: Mensaje más específico para usuarios nuevos vs reseteo
        const isWelcomeSetup = result.data.message?.includes('establecido') || 
                              result.data.message?.includes('configurado');
        
        const redirectMessage = isWelcomeSetup 
          ? 'Contraseña establecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.'
          : 'Contraseña restablecida exitosamente. Inicia sesión con tu nueva contraseña.';
        
        // Redireccionar al login después de 3 segundos
        setTimeout(() => {
          navigate('/login', { 
            state: { message: redirectMessage }
          });
        }, 3000);
      } else {
        setAlert({
          type: 'error',
          message: result.data.message || 'Error al restablecer la contraseña'
        });
      }
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.message || 'Error de conexión. Inténtalo nuevamente.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Mostrar loading mientras valida token
  if (validation.loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: BRAND_GRADIENT, display: 'flex', alignItems: 'center' }}>
      <Container component="main" maxWidth="sm">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Paper elevation={6} sx={{ p: 4, textAlign: 'center', borderRadius: 3, width: '100%' }}>
            <EapLogo variant="stacked" />
            <CircularProgress size={60} sx={{ mb: 2, mt: 3 }} />
            <Typography variant="h6">Validando enlace...</Typography>
            <Typography variant="body2" color="text.secondary">
              Por favor espera un momento
            </Typography>
          </Paper>
        </Box>
      </Container>
      </Box>
    );
  }

  // Mostrar error si token es inválido
  if (!validation.isValid) {
    return (
      <Box sx={{ minHeight: '100vh', background: BRAND_GRADIENT, display: 'flex', alignItems: 'center' }}>
      <Container component="main" maxWidth="sm">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Paper elevation={6} sx={{ p: 4, textAlign: 'center', borderRadius: 3, width: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <EapLogo variant="stacked" />
            </Box>
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              Enlace Inválido
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {validation.error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              • El enlace puede haber expirado (válido por 1 hora)<br/>
              • Es posible que ya hayas usado este enlace<br/>
              • Verifica que hayas copiado la URL completa
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/forgot-password')}
              sx={{ textTransform: 'none', mr: 2 }}
            >
              Solicitar Nuevo Enlace
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Ir al Login
            </Button>
          </Paper>
        </Box>
      </Container>
      </Box>
    );
  }

  // Mostrar formulario de restablecimiento
  return (
    <Box sx={{ minHeight: '100vh', background: BRAND_GRADIENT, display: 'flex', alignItems: 'center' }}>
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper 
          elevation={6}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ mb: 2 }}>
              <EapLogo variant="stacked" />
            </Box>
            {success ? (
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            ) : (
              <VpnKeyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            )}
            <Typography component="h1" variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {success ? '¡Contraseña Establecida!' : 'Establecer Contraseña'}
            </Typography>
            {validation.userEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Para: {validation.userEmail}
              </Typography>
            )}
            {!success && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Crea una contraseña segura para tu cuenta
              </Typography>
            )}
          </Box>

          {/* Alert */}
          {alert && (
            <Alert 
              severity={alert.type} 
              sx={{ width: '100%', mb: 3 }}
              onClose={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          )}

          {!success && (
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              {/* Nueva Contraseña */}
              <TextField
                fullWidth
                label="Nueva Contraseña"
                type={showPassword.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => {
                  setFormData({ ...formData, newPassword: e.target.value });
                  if (errors.newPassword) {
                    setErrors({ ...errors, newPassword: '' });
                  }
                }}
                onFocus={() => setPasswordFieldFocused(true)}
                onBlur={() => setPasswordFieldFocused(false)}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                margin="normal"
                autoFocus
                InputProps={{
                  startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Mostrar requisitos cuando hay texto O cuando está enfocado */}
              {(formData.newPassword || passwordFieldFocused) && (
                <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    Requisitos de contraseña:
                  </Typography>
                  {getPasswordRequirements(formData.newPassword).map((requirement, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {requirement.met ? (
                        <CheckIcon sx={{ color: 'success.main', fontSize: 18, mr: 1 }} />
                      ) : (
                        <CloseIcon sx={{ color: 'error.main', fontSize: 18, mr: 1 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: requirement.met ? 'success.main' : 'error.main',
                          fontWeight: requirement.met ? 500 : 400
                        }}
                      >
                        {requirement.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Indicador de fortaleza */}
              {formData.newPassword && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Fortaleza de contraseña
                    </Typography>
                    <Typography variant="caption" sx={{ color: getPasswordStrength(formData.newPassword).color }}>
                      {getPasswordStrength(formData.newPassword).label}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getPasswordStrength(formData.newPassword).strength}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getPasswordStrength(formData.newPassword).color
                      }
                    }}
                  />
                </Box>
              )}

              {/* Confirmar Contraseña */}
              <TextField
                fullWidth
                label="Confirmar Nueva Contraseña"
                type={showPassword.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => {
                  setFormData({ ...formData, confirmPassword: e.target.value });
                  if (errors.confirmPassword) {
                    setErrors({ ...errors, confirmPassword: '' });
                  }
                }}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                margin="normal"
                InputProps={{
                  startAdornment: <LockIcon sx={{ color: 'action.active', mr: 1 }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={
                  submitting || 
                  !formData.newPassword || 
                  !formData.confirmPassword ||
                  getPasswordRequirements(formData.newPassword).some(req => !req.met) ||
                  formData.newPassword !== formData.confirmPassword
                }
                startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                sx={{ 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  mb: 2
                }}
              >
                {submitting ? 'Estableciendo Contraseña...' : 'Establecer Contraseña'}
              </Button>
            </Box>
          )}

          {success && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                ¡Tu contraseña ha sido establecida exitosamente!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Serás redirigido al login en unos segundos...
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{ textTransform: 'none' }}
              >
                Ir al Login Ahora
              </Button>
            </Box>
          )}

          {/* Footer informativo */}
          {!success && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Después de establecer tu contraseña, podrás iniciar sesión normalmente
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
    </Box>
  );
};

export default ResetPassword;